import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Car, ChefHat, Download } from "lucide-react";

const BRAND_TEAL = "#0F5F63";
const CREAM = "#FDFBF7";
const ORANGE = "#E27B58";

const branchStyle = {
  full: { color: BRAND_TEAL, Icon: ChefHat, ringOpacity: 1 },
  drivethru: { color: ORANGE, Icon: Car, ringOpacity: 1 },
};

/**
 * Renders a single poster (visible preview) and exposes a hidden high-DPI
 * canvas we use to compose the final downloadable PNG.
 */
function PosterCard({ branch, labelPrimary, labelSecondary }) {
  const { t } = useLang();
  const { settings } = useSettings();
  const qrRef = useRef(null);
  const cfg = branchStyle[branch];
  const url = `${window.location.origin}/?branch=${branch}`;
  const brandName = settings.brand_name_en || "ZED";

  const download = () => {
    const qrCanvas = qrRef.current?.querySelector("canvas");
    if (!qrCanvas) return;

    // Compose poster at high DPI (1200x1600 → A-ish aspect, prints crisp on A4)
    const W = 1200, H = 1600;
    const out = document.createElement("canvas");
    out.width = W;
    out.height = H;
    const ctx = out.getContext("2d");

    // Background
    ctx.fillStyle = CREAM;
    ctx.fillRect(0, 0, W, H);

    // Top accent band
    ctx.fillStyle = cfg.color;
    ctx.fillRect(0, 0, W, 220);

    // Brand text on band
    ctx.fillStyle = CREAM;
    ctx.textAlign = "center";
    ctx.font = "600 96px Georgia, 'Times New Roman', serif";
    ctx.fillText(brandName.toUpperCase(), W / 2, 130);
    ctx.font = "500 22px Helvetica, Arial, sans-serif";
    ctx.letterSpacing = "0.35em";
    ctx.fillText("FRESH DONE RIGHT", W / 2, 180);

    // Branch label
    ctx.fillStyle = cfg.color;
    ctx.font = "600 64px Georgia, 'Times New Roman', serif";
    ctx.fillText(labelPrimary, W / 2, 350);
    ctx.fillStyle = "#5C6B6D";
    ctx.font = "500 22px Helvetica, Arial, sans-serif";
    ctx.fillText(labelSecondary.toUpperCase(), W / 2, 400);

    // QR panel (white card with border)
    const qrSize = 780;
    const qrX = (W - qrSize) / 2;
    const qrY = 470;
    ctx.fillStyle = "#ffffff";
    ctx.strokeStyle = cfg.color;
    ctx.lineWidth = 8;
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(qrX - 40, qrY - 40, qrSize + 80, qrSize + 80, 40);
    else ctx.rect(qrX - 40, qrY - 40, qrSize + 80, qrSize + 80);
    ctx.fill();
    ctx.stroke();

    // Draw QR
    ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

    // Scan caption
    ctx.fillStyle = BRAND_TEAL;
    ctx.font = "600 42px Georgia, 'Times New Roman', serif";
    ctx.fillText(t.qrScanCaption, W / 2, qrY + qrSize + 130);

    // URL
    ctx.fillStyle = "#5C6B6D";
    ctx.font = "500 22px 'Courier New', monospace";
    ctx.fillText(url, W / 2, qrY + qrSize + 180);

    // Footer
    ctx.fillStyle = cfg.color;
    ctx.fillRect(0, H - 20, W, 20);

    // Trigger download
    const link = document.createElement("a");
    link.download = `zed-poster-${branch}.png`;
    link.href = out.toDataURL("image/png");
    link.click();
  };

  const Icon = cfg.Icon;

  return (
    <div className="rounded-2xl bg-white border border-black/5 p-6" data-testid={`qr-poster-${branch}`}>
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: `${cfg.color}18`, color: cfg.color }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm font-medium text-[#1A1A1A]">{labelPrimary}</p>
          <p className="text-[10px] uppercase tracking-widest text-[#5C6B6D]">{labelSecondary}</p>
        </div>
      </div>

      <div
        ref={qrRef}
        className="rounded-xl p-6 flex items-center justify-center"
        style={{ background: CREAM, border: `2px solid ${cfg.color}` }}
      >
        <QRCodeCanvas
          value={url}
          size={220}
          bgColor={CREAM}
          fgColor={BRAND_TEAL}
          level="H"
          includeMargin={false}
        />
      </div>

      <p className="text-[10px] uppercase tracking-widest text-[#5C6B6D] mt-4 break-all" data-testid={`qr-poster-url-${branch}`}>
        {url}
      </p>

      <Button
        onClick={download}
        data-testid={`qr-download-${branch}`}
        className="mt-4 w-full rounded-full text-[#FDFBF7] uppercase tracking-widest text-xs h-11"
        style={{ background: cfg.color }}
      >
        <Download className="w-4 h-4 me-2" />
        {branch === "full" ? t.qrDownloadFull : t.qrDownloadDrive}
      </Button>
    </div>
  );
}

export default function QRPosters() {
  const { t, lang } = useLang();
  return (
    <div className="rounded-2xl bg-[#F4F2EB] p-6" data-testid="qr-posters-block">
      <div className="mb-6">
        <h3 className={`${lang === "ar" ? "font-serif-ar" : "font-serif-en"} text-2xl text-[#0F5F63] font-semibold`}>
          {t.qrPosterTitle}
        </h3>
        <p className="text-sm text-[#5C6B6D] mt-1">{t.qrPosterHint}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PosterCard branch="full" labelPrimary={t.branchFull} labelSecondary={t.fullBadge} />
        <PosterCard branch="drivethru" labelPrimary={t.branchDriveThru} labelSecondary={t.driveThruBadge} />
      </div>
    </div>
  );
}
